class AnimatedAccordion {
  constructor(container) {
    this.container = container;
    this.details = container.querySelector('details');
    this.summary = this.details?.querySelector('summary');
    this.content = this.summary?.nextElementSibling;
    this.contentInner = this.content?.querySelector('.accordion__content-inner') || this.content?.firstElementChild;
    this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.onSummaryClick = this.onSummaryClick.bind(this);
    this.onAnimationComplete = this.onAnimationComplete.bind(this);
  }

  init() {
    if (!this.details || !this.summary || !this.content || !this.contentInner || this.initialized) return;

    this.initialized = true;
    this.summary.setAttribute('aria-expanded', String(this.details.open));
    this.summary.addEventListener('click', this.onSummaryClick);
    this.container.__animatedAccordion = this;
  }

  destroy() {
    if (!this.initialized) return;

    this.summary.removeEventListener('click', this.onSummaryClick);
    this.content.removeEventListener('transitionend', this.onAnimationComplete);
    window.cancelAnimationFrame(this.animationFrame);
    this.container.__animatedAccordion = null;
  }

  onSummaryClick(event) {
    event.preventDefault();

    if (this.reducedMotionQuery.matches) {
      this.toggleWithoutAnimation();
      return;
    }

    if (this.details.classList.contains('is-animating')) {
      if (this.isOpening) {
        this.close();
      } else {
        this.open();
      }
      return;
    }

    if (this.details.open) {
      this.close();
      return;
    }

    this.open();
  }

  toggleWithoutAnimation() {
    const shouldOpen = !this.details.open;

    this.content.removeEventListener('transitionend', this.onAnimationComplete);
    window.cancelAnimationFrame(this.animationFrame);
    this.clearInlineStyles();

    this.details.classList.remove('is-animating');
    this.details.open = shouldOpen;
    this.summary.setAttribute('aria-expanded', String(shouldOpen));
  }

  open() {
    const startHeight = this.details.classList.contains('is-animating')
      ? this.content.getBoundingClientRect().height
      : 0;
    const computedStyles = window.getComputedStyle(this.contentInner);
    const startOpacity = this.details.classList.contains('is-animating') ? computedStyles.opacity : '0';
    const startTransform =
      this.details.classList.contains('is-animating') && computedStyles.transform !== 'none'
        ? computedStyles.transform
        : 'translateY(-0.4rem)';

    this.content.removeEventListener('transitionend', this.onAnimationComplete);
    window.cancelAnimationFrame(this.animationFrame);

    this.details.open = true;
    this.summary.setAttribute('aria-expanded', 'true');
    this.isOpening = true;

    this.prepareAnimation(startHeight, startOpacity, startTransform);

    const endHeight = this.content.scrollHeight;
    this.animationDuration = this.getDuration(endHeight - startHeight);

    this.animationFrame = window.requestAnimationFrame(() => {
      this.content.style.transition = `height ${this.animationDuration}ms cubic-bezier(0.22, 1, 0.36, 1)`;
      this.contentInner.style.transition =
        `opacity ${Math.max(this.animationDuration - 40, 160)}ms ease, ` +
        `transform ${this.animationDuration}ms cubic-bezier(0.22, 1, 0.36, 1)`;
      this.content.style.height = `${endHeight}px`;
      this.contentInner.style.opacity = '1';
      this.contentInner.style.transform = 'translateY(0)';
    });

    this.content.addEventListener('transitionend', this.onAnimationComplete);
  }

  close() {
    const startHeight = this.content.getBoundingClientRect().height || this.content.scrollHeight;
    const computedStyles = window.getComputedStyle(this.contentInner);
    const startOpacity = computedStyles.opacity;
    const startTransform = computedStyles.transform !== 'none' ? computedStyles.transform : 'translateY(0)';

    this.content.removeEventListener('transitionend', this.onAnimationComplete);
    window.cancelAnimationFrame(this.animationFrame);

    this.summary.setAttribute('aria-expanded', 'false');
    this.isOpening = false;

    this.prepareAnimation(startHeight, startOpacity, startTransform);

    this.animationDuration = this.getDuration(startHeight);

    this.animationFrame = window.requestAnimationFrame(() => {
      this.content.style.transition = `height ${this.animationDuration}ms cubic-bezier(0.4, 0, 0.2, 1)`;
      this.contentInner.style.transition =
        `opacity ${Math.max(this.animationDuration - 90, 120)}ms ease, ` +
        `transform ${this.animationDuration}ms cubic-bezier(0.4, 0, 0.2, 1)`;
      this.content.style.height = '0px';
      this.contentInner.style.opacity = '0';
      this.contentInner.style.transform = 'translateY(-0.4rem)';
    });

    this.content.addEventListener('transitionend', this.onAnimationComplete);
  }

  prepareAnimation(startHeight, startOpacity, startTransform) {
    this.details.classList.add('is-animating');
    this.content.style.height = `${startHeight}px`;
    this.content.style.overflow = 'hidden';
    this.content.style.transition = 'none';
    this.contentInner.style.opacity = startOpacity;
    this.contentInner.style.transform = startTransform;
    this.contentInner.style.transition = 'none';

    // Force the browser to apply the starting state before transitioning.
    this.content.offsetHeight;
  }

  onAnimationComplete(event) {
    if (event.target !== this.content || event.propertyName !== 'height') return;

    this.content.removeEventListener('transitionend', this.onAnimationComplete);
    window.cancelAnimationFrame(this.animationFrame);

    const shouldRemainOpen = this.isOpening;
    this.details.open = shouldRemainOpen;
    this.details.classList.remove('is-animating');
    this.isOpening = shouldRemainOpen;
    this.clearInlineStyles();
    this.summary.setAttribute('aria-expanded', String(shouldRemainOpen));
  }

  clearInlineStyles() {
    this.content.style.height = '';
    this.content.style.overflow = '';
    this.content.style.transition = '';
    this.contentInner.style.opacity = '';
    this.contentInner.style.transform = '';
    this.contentInner.style.transition = '';
  }

  getDuration(delta) {
    return Math.max(220, Math.min(360, Math.round(Math.abs(delta) * 0.65)));
  }
}

function initAnimatedAccordions(root = document) {
  root.querySelectorAll('[data-animated-accordion]').forEach((container) => {
    if (container.__animatedAccordion) return;

    const accordion = new AnimatedAccordion(container);
    accordion.init();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initAnimatedAccordions(), { once: true });
} else {
  initAnimatedAccordions();
}

document.addEventListener('shopify:section:load', (event) => {
  initAnimatedAccordions(event.target);
});
